import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bus,
  Calendar,
  CheckCircle2,
  Copy,
  Check as CheckIcon,
  X as XIcon,
  AlertCircle,
  Search,
} from 'lucide-react';
import api from '../../services/api';
import { createBookingGroup } from '../../services/bookings';
import { listStops } from '../../services/stops';
import { searchTrips, isUpcomingTrip } from '../../services/trips';
import Button from '../../components/shared/Button/Button';
import SearchableSelect from '../../components/shared/SearchableSelect/SearchableSelect';
import Input from '../../components/shared/Input/Input';
import FormField from '../../components/shared/FormField/FormField';
import LoadingSpinner from '../../components/shared/LoadingSpinner/LoadingSpinner';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import MatatuSeatMap, { isMatatuLayout } from '../../components/shared/MatatuSeatMap';
import type { Stop, Trip, Seat, TripSearchResult, RouteStop, TripFare } from '../../types/api';
import { useAuth } from '../../context/AuthContext';
import { seatLabel } from '../../utils/seatLabel';
import { CheckoutPanel } from './checkout/CheckoutPanel';

interface PassengerForm {
  name: string;
  phone: string;
  id_number: string;
}

const emptyForm: PassengerForm = { name: '', phone: '', id_number: '' };

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatTripDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---------------------------------------------------------------------------
// Sub-route search form + result cards
// ---------------------------------------------------------------------------

interface StopSearchFormProps {
  stops: Stop[];
  onResults: (results: TripSearchResult[], fromId: number, toId: number) => void;
  onClear: () => void;
  hasResults: boolean;
}

export const StopSearchForm: React.FC<StopSearchFormProps> = ({
  stops,
  onResults,
  onClear,
  hasResults,
}) => {
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [date, setDate] = useState<string>(todayIso());
  const [loading, setLoading] = useState(false);

  // If the chosen "From" matches the current "To", the To option is filtered
  // out — clear the now-invalid To so a stale value can't be submitted.
  useEffect(() => {
    if (to !== null && to === from) setTo(null);
  }, [from, to]);

  const doSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!from || !to || !date) return;
    setLoading(true);
    try {
      const results = await searchTrips({ from, to, date });
      onResults(results, from, to);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFrom(null);
    setTo(null);
    setDate(todayIso());
    onClear();
  };

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-semibold text-slate-800">Find by stop</p>
      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={doSearch}
      >
        <div className="flex-1">
          <p className="mb-1.5 text-xs font-medium text-slate-600">From</p>
          <SearchableSelect
            aria-label="From stop"
            placeholder="Choose stop…"
            searchPlaceholder="Search stops…"
            value={from ? String(from) : ''}
            onChange={(v) => setFrom(v ? parseInt(v, 10) : null)}
            options={stops.map((s) => ({ value: String(s.id), label: s.name }))}
          />
        </div>

        <div className="flex-1">
          <p className="mb-1.5 text-xs font-medium text-slate-600">To</p>
          <SearchableSelect
            aria-label="To stop"
            placeholder="Choose stop…"
            searchPlaceholder="Search stops…"
            value={to ? String(to) : ''}
            onChange={(v) => setTo(v ? parseInt(v, 10) : null)}
            options={stops
              .filter((s) => s.id !== from)
              .map((s) => ({ value: String(s.id), label: s.name }))}
          />
        </div>

        <div className="flex-1">
          <p className="mb-1.5 text-xs font-medium text-slate-600">Date</p>
          <input
            type="date"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
            value={date}
            min={todayIso()}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="flex shrink-0 gap-2">
          <Button
            type="submit"
            disabled={!from || !to || !date || loading}
            loading={loading}
            icon={<Search className="h-4 w-4" />}
          >
            {loading ? 'Searching…' : 'Search'}
          </Button>
          {hasResults && (
            <Button variant="secondary" type="button" onClick={handleClear}>
              Clear
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

interface TripResultCardProps {
  result: TripSearchResult;
  fromId: number;
  toId: number;
  onBook: () => void;
}

const TripResultCard: React.FC<TripResultCardProps> = ({ result, fromId, toId, onBook }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="font-semibold text-slate-900">{result.route_name}</div>
        <div className="mt-1 inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">
          {result.vehicle_reg}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-lg font-bold tabular-nums text-brand-600">
          KES {Number(result.segment_fare).toLocaleString()}
        </div>
        <SeatPill available={result.available_seats} />
      </div>
    </div>

    {/* Stop sequence with from/to highlighted */}
    <div className="mt-4 flex flex-wrap items-center gap-1.5 text-sm">
      {result.stops.map((s, i) => (
        <React.Fragment key={s.id}>
          {i > 0 && (
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
          )}
          <span
            className={
              s.id === fromId || s.id === toId
                ? 'rounded-md bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700'
                : 'text-xs text-slate-600'
            }
          >
            {s.name}
          </span>
        </React.Fragment>
      ))}
    </div>

    <div className="mt-4 flex items-center justify-between text-sm">
      <span className="text-slate-600">
        Boards at{' '}
        <span className="font-medium text-slate-900">
          {new Date(result.boarding_eta).toLocaleString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </span>
      <button
        type="button"
        onClick={onBook}
        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
      >
        Book
        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const Trips: React.FC = () => {
  const auth = useAuth();
  const isAuthenticated = !!auth.user;
  const navigate = useNavigate();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [stops, setStops] = useState<Stop[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [seatsLoading, setSeatsLoading] = useState(false);
  const [selectedSeatIds, setSelectedSeatIds] = useState<number[]>([]);
  // Stops + fare to use for the in-page booking (origin→destination when
  // browsing, or the searched from/to when booking from a search result).
  const [bookingStops, setBookingStops] = useState<{ boardingId: number; alightingId: number } | null>(null);
  const [bookingFare, setBookingFare] = useState<string | null>(null);
  const [form, setForm] = useState<PassengerForm>(emptyForm);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<{ groupReference: string; total: string } | null>(null);
  const [refCopied, setRefCopied] = useState(false);
  const [checkout, setCheckout] = useState<{
    group: { id: number; reference: string; total: string; paybill: string; account_reference: string };
    heldUntil: string | null;
    summary: { departure: string; boardingName: string; alightingName: string; seatLabels: string; fare: string };
    bookingPhone: string;
  } | null>(null);

  // Sub-route search results
  const [searchResults, setSearchResults] = useState<TripSearchResult[] | null>(null);
  const [searchFromId, setSearchFromId] = useState<number>(0);
  const [searchToId, setSearchToId] = useState<number>(0);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const res = await api.get('/trips/');
      const list: Trip[] = Array.isArray(res.data)
        ? res.data
        : res.data.results || [];
      // Only show upcoming trips (sold-out included). The endpoint returns all
      // trips including past ones — status isn't auto-advanced when departure
      // passes, so stale scheduled trips would otherwise appear here.
      setTrips(list.filter((t) => isUpcomingTrip(t)));
    } catch {
      setTrips([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTrips();
    listStops(true).then(setStops).catch(() => setStops([]));
  }, []);

  const selectTrip = async (
    trip: Trip,
    stops?: { boardingId: number; alightingId: number },
  ) => {
    if (trip.available_seats <= 0) return;
    setSelectedTrip(trip);
    setSelectedSeatIds([]);
    setBookingError(null);
    setBookingSuccess(null);
    setSeatsLoading(true);
    try {
      const res = await api.get(`/trips/${trip.id}/`);
      const detail = res.data;
      setSeats(detail.seats || []);
      // Resolve the boarding/alighting pair: use the searched stops if given,
      // otherwise default to the route's origin → destination (whole trip).
      const routeStops = [...((detail.route?.stops as RouteStop[]) ?? [])].sort(
        (a, b) => a.sequence - b.sequence,
      );
      let pair = stops ?? null;
      if (!pair && routeStops.length >= 2) {
        pair = {
          boardingId: routeStops[0].stop_id,
          alightingId: routeStops[routeStops.length - 1].stop_id,
        };
      }
      setBookingStops(pair);
      const tf = (detail.trip_fares as TripFare[] | undefined)?.find(
        (f) => pair && f.boarding_stop_id === pair.boardingId && f.alighting_stop_id === pair.alightingId,
      );
      setBookingFare(tf?.amount ?? detail.fare ?? String(trip.fare));
    } catch {
      setSeats([]);
      setBookingStops(null);
      setBookingFare(null);
    }
    setSeatsLoading(false);
  };

  const bookFromSearch = (result: TripSearchResult) => {
    const pseudoTrip = {
      id: result.id,
      route_name: result.route_name,
      departure_time: result.departure_time,
      fare: result.segment_fare,
      available_seats: result.available_seats,
    } as unknown as Trip;
    selectTrip(pseudoTrip, { boardingId: searchFromId, alightingId: searchToId });
  };

  const toggleSeat = (seat: Seat) => {
    if (seat.status !== 'available') return;
    setSelectedSeatIds((prev) =>
      prev.includes(seat.id)
        ? prev.filter((id) => id !== seat.id)
        : [...prev, seat.id],
    );
  };

  const handleBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTrip || selectedSeatIds.length === 0) return;
    if (!bookingStops) {
      setBookingError('Could not determine boarding/alighting stops for this trip.');
      return;
    }
    setBookingLoading(true);
    setBookingError(null);
    try {
      const result = await createBookingGroup({
        trip: selectedTrip.id,
        seats: selectedSeatIds,
        boarding_stop_id: bookingStops.boardingId,
        alighting_stop_id: bookingStops.alightingId,
        passenger_name: form.name,
        passenger_phone: form.phone,
        passenger_id_number: form.id_number,
      });
      const seatLabels = seats
        .filter((s) => selectedSeatIds.includes(s.id))
        .map((s) => seatLabel(s.seat_number))
        .join(', ');
      setCheckout({
        group: {
          id: result.group_id,
          reference: result.group_reference,
          total: result.total,
          paybill: result.paybill,
          account_reference: result.account_reference,
        },
        heldUntil: result.held_until,
        summary: {
          departure: selectedTrip.departure_time,
          boardingName: stops.find((s) => s.id === bookingStops!.boardingId)?.name ?? '',
          alightingName: stops.find((s) => s.id === bookingStops!.alightingId)?.name ?? '',
          seatLabels,
          fare: bookingFare ?? result.total,
        },
        bookingPhone: form.phone,
      });
      setSelectedSeatIds([]);
      setForm(emptyForm);
    } catch {
      setBookingError('Booking failed. Please try again.');
    }
    setBookingLoading(false);
  };

  const handleCopyRef = async () => {
    if (!bookingSuccess) return;
    await navigator.clipboard.writeText(bookingSuccess.groupReference);
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 1500);
  };

  const handleBookAnother = () => {
    setBookingSuccess(null);
    setSelectedTrip(null);
    setSeats([]);
    setSelectedSeatIds([]);
  };


  const handleSearchResults = (
    results: TripSearchResult[],
    fromId: number,
    toId: number,
  ) => {
    setSearchResults(results);
    setSearchFromId(fromId);
    setSearchToId(toId);
  };

  const handleSearchClear = () => {
    setSearchResults(null);
    setSearchFromId(0);
    setSearchToId(0);
  };

  const showSearchResults = searchResults !== null;

  if (checkout) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12">
        <CheckoutPanel
          group={checkout.group}
          heldUntil={checkout.heldUntil}
          summary={checkout.summary}
          isAuthenticated={isAuthenticated}
          bookingPhone={checkout.bookingPhone}
          onPaid={() => {
            setBookingSuccess({ groupReference: checkout.group.reference, total: checkout.group.total });
            setCheckout(null);
            fetchTrips();
          }}
          onExpired={() => {
            setCheckout(null);
            setSelectedTrip(null);
            setSelectedSeatIds([]);
            fetchTrips();
          }}
          onLogin={() => navigate('/login')}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Available trips
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Pick a trip, choose your seat, and confirm in under a minute.
        </p>
      </header>

      {/* Sub-route search (from/to/date) */}
      <StopSearchForm
        stops={stops}
        onResults={handleSearchResults}
        onClear={handleSearchClear}
        hasResults={showSearchResults}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column: search results OR all-trips grid */}
        <div className="lg:col-span-2">
          {showSearchResults ? (
            /* ── Sub-route search results ── */
            <div className="space-y-4">
              {searchResults!.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-4">
                  <EmptyState
                    icon={<Search className="h-6 w-6" aria-hidden />}
                    title="No matching trips"
                    description="Try a different stop combination or date."
                  />
                </div>
              ) : (
                searchResults!.map((r) => (
                  <TripResultCard
                    key={r.id}
                    result={r}
                    fromId={searchFromId}
                    toId={searchToId}
                    onBook={() => bookFromSearch(r)}
                  />
                ))
              )}
            </div>
          ) : (
            /* ── All-trips grid (existing behaviour) ── */
            <>
              {loading ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-16">
                  <LoadingSpinner centered label="Loading trips…" />
                </div>
              ) : trips.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-4">
                  <EmptyState
                    icon={<Calendar className="h-6 w-6" aria-hidden />}
                    title="No trips found"
                    description="Check back soon — new trips are scheduled regularly."
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {trips.map((trip) => {
                    const isSelected = selectedTrip?.id === trip.id;
                    const isSoldOut = trip.available_seats <= 0;
                    return (
                      <button
                        key={trip.id}
                        type="button"
                        onClick={() => selectTrip(trip)}
                        disabled={isSoldOut}
                        className={[
                          'group relative flex flex-col rounded-2xl border bg-white p-5 text-left shadow-sm transition-all',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2',
                          isSelected
                            ? 'border-brand-600 ring-2 ring-brand-600/20'
                            : 'border-slate-200 hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md',
                          isSoldOut &&
                            'cursor-not-allowed opacity-60 hover:translate-y-0 hover:shadow-sm',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-3">
                              <div>
                                <div className="text-xs text-slate-500">From</div>
                                <div className="font-semibold text-slate-900">
                                  {trip.origin_name ?? '—'}
                                </div>
                              </div>
                              <div className="text-slate-400">→</div>
                              <div>
                                <div className="text-xs text-slate-500">To</div>
                                <div className="font-semibold text-slate-900">
                                  {trip.destination_name ?? '—'}
                                </div>
                              </div>
                            </div>
                            <p className="mt-1 text-sm text-slate-600">
                              {formatTripDate(trip.departure_time)}
                            </p>
                          </div>
                          <Bus
                            className="h-5 w-5 text-slate-400 group-hover:text-brand-600"
                            aria-hidden
                          />
                        </div>

                        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs">
                            {trip.vehicle_reg ?? '—'}
                          </span>
                          <span>·</span>
                          <span>{trip.driver_name ?? 'Driver TBA'}</span>
                        </div>

                        <div className="mt-4 flex items-end justify-between">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                              Fare
                            </p>
                            <p className="mt-0.5 text-xl font-bold tabular-nums text-brand-700">
                              KES {Number(trip.fare).toLocaleString()}
                            </p>
                          </div>
                          <SeatPill available={trip.available_seats} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Booking panel (1/3 on lg, sticky) — works in browse and search modes */}
        <aside className="lg:sticky lg:top-24 lg:h-fit">
            {bookingSuccess ? (
              <SuccessPanel
                groupReference={bookingSuccess.groupReference}
                total={bookingSuccess.total}
                onCopy={handleCopyRef}
                copied={refCopied}
                onAgain={handleBookAnother}
              />
            ) : !selectedTrip ? (
              showSearchResults ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                    <Search className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">
                    {searchResults!.length} trip
                    {searchResults!.length !== 1 ? 's' : ''} found
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Tap <strong>Book</strong> on a result to choose a seat.
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                    <Bus className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">
                    Pick a trip to begin
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Select an available trip on the left to choose a seat and book.
                  </p>
                </div>
              )
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <header className="border-b border-slate-200 px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Booking
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-900">
                    {selectedTrip.route_name}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {formatTripDate(selectedTrip.departure_time)} · KES{' '}
                    {Number(bookingFare ?? selectedTrip.fare).toLocaleString()}
                  </p>
                </header>

                <div className="border-b border-slate-200 p-5">
                  <p className="text-sm font-semibold text-slate-800">Pick a seat</p>
                  {seatsLoading ? (
                    <div className="py-6">
                      <LoadingSpinner centered label="Loading seats…" />
                    </div>
                  ) : seats.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">
                      No seat data available for this trip.
                    </p>
                  ) : isMatatuLayout(seats.length) ? (
                    <MatatuSeatMap
                      seats={seats}
                      selectedSeatIds={selectedSeatIds}
                      onToggle={toggleSeat}
                    />
                  ) : (
                    <>
                      <div className="mt-3 grid grid-cols-5 gap-2 sm:grid-cols-6">
                        {seats.map((seat) => (
                          <SeatButton
                            key={seat.id}
                            seat={seat}
                            selected={selectedSeatIds.includes(seat.id)}
                            onSelect={() => {
                              if (seat.status !== 'available') return;
                              toggleSeat(seat);
                            }}
                          />
                        ))}
                      </div>
                      <SeatLegend />
                    </>
                  )}
                </div>

                {selectedSeatIds.length > 0 && (
                  <form onSubmit={handleBook} className="flex flex-col gap-4 p-5">
                    <p className="text-sm font-semibold text-slate-800">
                      Passenger details — {selectedSeatIds.length} seat{selectedSeatIds.length !== 1 ? 's' : ''} selected
                    </p>
                    <FormField label="Full name" name="name" required>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                      />
                    </FormField>
                    <FormField label="Phone number" name="phone" required>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                        required
                      />
                    </FormField>
                    <FormField
                      label="ID number"
                      name="id_number"
                      hint="Optional — for ticket verification."
                    >
                      <Input
                        id="id_number"
                        value={form.id_number}
                        onChange={(e) =>
                          setForm({ ...form, id_number: e.target.value })
                        }
                      />
                    </FormField>

                    {bookingError && (
                      <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-800">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        <span>{bookingError}</span>
                      </div>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      loading={bookingLoading}
                      disabled={selectedSeatIds.length === 0 || !bookingStops}
                      icon={<ArrowRight className="h-4 w-4" />}
                    >
                      {bookingLoading
                        ? 'Booking…'
                        : `Book ${selectedSeatIds.length} seat${selectedSeatIds.length !== 1 ? 's' : ''} — KES ${(selectedSeatIds.length * Number(bookingFare ?? selectedTrip.fare)).toLocaleString()}`}
                    </Button>
                  </form>
                )}
              </div>
            )}
        </aside>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const SeatPill: React.FC<{ available: number }> = ({ available }) => {
  if (available <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden />
        Sold out
      </span>
    );
  }
  if (available <= 5) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
        {available} left
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
      {available} seats
    </span>
  );
};

interface SeatButtonProps {
  seat: Seat;
  selected: boolean;
  onSelect: () => void;
}

const SeatButton: React.FC<SeatButtonProps> = ({ seat, selected, onSelect }) => {
  const available = seat.status === 'available';
  return (
    <button
      type="button"
      disabled={!available}
      onClick={onSelect}
      aria-label={`Seat ${seatLabel(seat.seat_number)}, ${seat.status}${selected ? ', selected' : ''}`}
      className={[
        'relative flex h-12 w-12 items-center justify-center rounded-lg border text-sm font-bold transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-1',
        selected && 'border-brand-700 bg-brand-600 text-white ring-2 ring-brand-700',
        !selected &&
          available &&
          'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-400',
        !selected &&
          !available &&
          'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {selected ? (
        <CheckIcon className="h-4 w-4" aria-hidden />
      ) : !available ? (
        <XIcon className="h-3.5 w-3.5" aria-hidden />
      ) : null}
      <span className={selected || !available ? 'sr-only' : ''}>
        {seatLabel(seat.seat_number)}
      </span>
      {(selected || !available) && (
        <span className="absolute bottom-0.5 right-1 text-[10px] font-bold opacity-80">
          {seatLabel(seat.seat_number)}
        </span>
      )}
      {seat.is_window && (
        <span
          title="Window seat"
          aria-label="Window seat"
          className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-sky-500"
        />
      )}
    </button>
  );
};

const SeatLegend: React.FC = () => (
  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3 w-3 rounded border border-emerald-200 bg-emerald-50" aria-hidden />
      Available
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3 w-3 rounded border border-brand-700 bg-brand-600" aria-hidden />
      Selected
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3 w-3 rounded border border-slate-200 bg-slate-100" aria-hidden />
      Booked
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-1.5 w-1.5 rounded-full bg-sky-500" aria-hidden />
      Window
    </span>
  </div>
);

interface SuccessPanelProps {
  groupReference: string;
  total: string;
  onCopy: () => void;
  copied: boolean;
  onAgain: () => void;
}

const SuccessPanel: React.FC<SuccessPanelProps> = ({
  groupReference,
  total,
  onCopy,
  copied,
  onAgain,
}) => (
  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center shadow-sm">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
      <CheckCircle2 className="h-7 w-7" aria-hidden />
    </div>
    <h3 className="mt-4 text-xl font-bold text-emerald-900">Booked!</h3>
    <p className="mt-1 text-sm text-emerald-800">
      Save your group reference — you'll need it at boarding.
    </p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <span className="rounded-lg border border-emerald-300 bg-white px-3 py-2 font-mono text-base font-bold text-emerald-900">
        {groupReference}
      </span>
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy reference"
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100"
      >
        {copied ? <CheckIcon className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
    <p className="mt-3 text-sm font-semibold text-emerald-900">
      Total: KES {Number(total).toLocaleString()}
    </p>
    <div className="mt-6">
      <Button variant="secondary" onClick={onAgain} fullWidth>
        Book another
      </Button>
    </div>
  </div>
);

export default Trips;
